import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';
import { ApiResponse } from '../types';
import API_CONFIG from '../config/api';

// Create a function to handle navigation
let navigationHandler = (path: string) => {
  window.location.href = path;
};

// Export a function to set the navigation handler
export const setNavigationHandler = (handler: (path: string) => void) => {
  navigationHandler = handler;
};

const apiClient = axios.create({
  baseURL: API_CONFIG.BASE_URL_WITH_VERSION,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add authorization header to all requests
apiClient.interceptors.request.use(
  config => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
      console.log(`Request to ${config.url} with auth token`);
    } else {
      console.log(`Request to ${config.url} without auth token`);
    }
    return config;
  },
  error => Promise.reject(error)
);

// Handle response errors
apiClient.interceptors.response.use(
  response => {
    console.log(`Response from ${response.config.url}:`, response.status);
    return response;
  },
  (error: AxiosError) => {
    console.error('API Error:', error.message, error.response?.status, error.config?.url);

    // Silently handle token validation requests
    if (error.config?.headers?.['X-Request-Type'] === 'token-validation') {
      console.log('Silently handling token validation error');
      return Promise.reject(new Error(''));
    }

    // Don't display 401 errors on login page unless it's specifically for the login endpoint
    if (error.response?.status === 401) {
      const nonAuthPaths = [
        '/login',
        '/register',
        '/forgot-password',
        '/reset-password',
        '/finish-formalization',
      ];
      const currentPath = window.location.pathname;

      // Special handling for login page - only pass through error for the actual login endpoint
      if (currentPath === '/login' && error.config?.url !== '/auth/login') {
        console.log('Ignoring 401 error on login page for non-login endpoint:', error.config?.url);
        return Promise.reject(new Error('')); // Return empty error for non-login requests
      }

      // Check if we're on a non-auth path that should allow 401 errors
      if (nonAuthPaths.some(path => currentPath.startsWith(path))) {
        console.log('401 on non-auth path, not redirecting but clearing token');
        localStorage.removeItem('token');
      } else {
        // On a protected path, redirect to login
        console.log('401 Unauthorized - redirecting to login');

        // Clear all auth data
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        localStorage.removeItem('rememberMe');

        // FIXED: Only redirect if not already on login page
        // This prevents redirect loops
        if (currentPath !== '/login') {
          console.log('Redirecting to login page');
          navigationHandler('/login');
        } else {
          console.log('Already on login page, not redirecting to prevent loop');
        }
      }
    }

    return Promise.reject(error);
  }
);

// Generic get method
export const get = async <T>(url: string, config?: AxiosRequestConfig): Promise<T> => {
  try {
    const response: AxiosResponse<T> = await apiClient.get(url, config);
    console.log(`GET ${url} response data:`, response.data);
    return response.data;
  } catch (error) {
    throw handleApiError(error as AxiosError);
  }
};

// Generic post method
export const post = async <T, D>(url: string, data: D, config?: AxiosRequestConfig): Promise<T> => {
  try {
    const response: AxiosResponse<T> = await apiClient.post(url, data, config);
    return response.data;
  } catch (error) {
    throw handleApiError(error as AxiosError);
  }
};

// Generic put method
export const put = async <T, D>(url: string, data: D, config?: AxiosRequestConfig): Promise<T> => {
  try {
    const response: AxiosResponse<T> = await apiClient.put(url, data, config);
    return response.data;
  } catch (error) {
    throw handleApiError(error as AxiosError);
  }
};

// Generic patch method
export const patch = async <T, D>(
  url: string,
  data: D,
  config?: AxiosRequestConfig
): Promise<T> => {
  try {
    const response: AxiosResponse<T> = await apiClient.patch(url, data, config);
    return response.data;
  } catch (error) {
    throw handleApiError(error as AxiosError);
  }
};

// Generic delete method
export const del = async <T>(url: string, config?: AxiosRequestConfig): Promise<T> => {
  try {
    const response: AxiosResponse<T> = await apiClient.delete(url, config);
    return response.data;
  } catch (error) {
    throw handleApiError(error as AxiosError);
  }
};

// Error handling helper
const handleApiError = (error: AxiosError): Error => {
  console.log('Handling API error:', error);

  // For 401 errors on login page, provide a friendly message
  if (error.response?.status === 401 && window.location.pathname === '/login') {
    return new Error(
      'Ops! Não foi possível acessar sua conta. Verifique seu e-mail e senha e tente novamente.'
    );
  }

  // Extract a friendly message, even when detail is an object
  const data: any = error.response?.data;
  const detail = data?.detail;
  let extractedDetail = '';

  if (detail && typeof detail === 'object') {
    extractedDetail = detail.message || detail.error || detail.error_description || '';
  } else if (typeof detail === 'string') {
    extractedDetail = detail;
  }

  const message =
    extractedDetail ||
    (typeof data?.message === 'string' ? data.message : '') ||
    (typeof error.message === 'string' ? error.message : '') ||
    'Ocorreu um erro inesperado. Por favor, tente novamente.';

  return new Error(message);
};

export default {
  get,
  post,
  put,
  patch,
  del,
};
